import React from 'react';
import ReactDOM from 'react-dom';

import OpioidFilters from './components/filters';
import OpioidTable from './components/table';
import getFilteredData from './services/getFilteredData';
import sortRowsByField from './services/sortRowsByField';

/**
 * Opioid Prescribers app component.
 */
const appElement = document.getElementById('opioid-prescribers-interactive');

class OpioidPrescribers extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onFilter = this.onFilter.bind(this);
    this.onResetFilters = this.onResetFilters.bind(this);
    this.handleTableSortClick =
      this.handleTableSortClick.bind(this);
    this.state = {
      activeProcedure: 'Coronary artery bypass grafting (CABG)',
      activeSearch: null,
      activeState: 'all',
      data: [],
      loading: true,
      procedures: [],
      tableData: [],
      states: [],
      sortedColumn: 'RATE',
      sortOrder: 'DESC',
    };
  }

  componentDidMount() {
    this.getData();
  }
  /**
   * Handles filtering of data table
   * @param {string} type - the filter type (procedure, search, state)
   * @param {string|number} value - the string or parameter by which to filter
   */
  onFilter(type, value) { // eslint-disable-line class-methods-use-this
    const filter = ('all' === value || '' === value) ? undefined : value;
    this.setState((prevState) => ({
      [type]: value,
      tableData: this.filterAndSort(
        prevState.data,
        ('activeProcedure' === type) ? filter : prevState.activeProcedure,
        prevState.sortedColumn,
        ('activeSearch' === type) ? filter : prevState.activeSearch,
        ('activeState' === type) ? value : prevState.activeState,
        prevState.sortOrder
      ),
    }));
  }
  /**
   * Handles resetting data table filters and sorting
   */
  onResetFilters() {
    this.setState((prevState) => ({
      activeState: 'all',
      activeSearch: null,
      loading: false,
      sortedColumn: 'RATE',
      sortOrder: 'DESC',
      tableData: this.filterAndSort(
        prevState.data,
        prevState.activeProcedure,
        'RATE',
        null,
        'all',
        'DESC'
      ),
    }));
  }

  /**
   * Gets application data from endpoints
   */
  getData() {
    fetch('/wp-json/khn/v1/data-source/opioid-prescribers')
      .then((response) => response.json())
      .then((data) => JSON.parse(data))
      .then((data) => this.formatData(data));
  }

  /**
   * Formats, defaults, and assigns data to state.
   * @param {object} data - Data to be formatted.
   */
  formatData(data) {
    const states = [];
    const procedures = [];
    data.forEach((dat) => {
      states.push(dat.st);
      procedures.push(dat.pcd);
    });
    const uniqueProcedures = [...new Set(procedures)].sort();
    const allStates = [...new Set(states)]
      .filter((state) => 'PR' !== state && 'VI' !== state)
      .sort();
    this.setState(() => ({
      data,
      loading: false,
      procedures: uniqueProcedures,
      states: allStates,
      tableData: this.filterAndSort(
        data,
        this.state.activeProcedure,
        this.state.sortedColumn,
        this.state.activeSearch,
        this.state.activeState,
        this.state.sortOrder
      ),
    }));
  }

  /**
   * Shorthand to filters and sort table data
   * @param {Array} dataArray - Data to be filtered and sorted.
   * @param {string} activeProcedure - Active procedure type.
   * @param {string} sortedColumn - Column being sorted.
   * @param {string} activeSearch - Search string.
   * @param {string} activeState - State by which to filter.
   * @param {string} sortOrder - 'ASC' or 'DESC'.
   */
  filterAndSort( // eslint-disable-line class-methods-use-this
    dataArray,
    activeProcedure,
    sortedColumn,
    activeSearch,
    activeState,
    sortOrder
  ) {
    const filteredData = getFilteredData(
      dataArray,
      activeProcedure,
      activeSearch,
      activeState
    );
    return sortRowsByField(
      filteredData,
      sortedColumn,
      sortOrder
    );
  }
  /**
   * Click listener for sorting table columns.
   * @param {number|string} column - The column for which to sort contributions.
   * @param {string} order - 'ASC' or 'DESC'.
   */
  handleTableSortClick(column, order) { // eslint-disable-line class-methods-use-this
    this.setState((prevState) => ({
      loading: false,
      tableData: sortRowsByField(
        prevState.tableData,
        column,
        order
      ),
      sortedColumn: column,
      sortOrder: order,
    }));
  }
  /**
   * @returns {object} Component markup.
   */
  render() {
    const {
      activeProcedure,
      activeSearch,
      activeState,
      loading,
      procedures,
      states,
      sortedColumn,
      sortOrder,
      tableData,
    } = this.state;
    const sourceText = 'Johns Hopkins-KFF Health News analysis of claims data from the Centers for Medicare & Medicaid Services'; // eslint-disable-line max-len
    if (loading) {
      return (
        <div className="loading">
          Loading...
        </div>
      );
    }
    return (
      <div className="opioidPrescribers">
        <h2>Look Up Surgeons</h2>
        <OpioidFilters
          activeProcedure={activeProcedure}
          activeSearch={activeSearch}
          activeState={activeState}
          onFilter={this.onFilter}
          procedures={procedures}
          states={states}
        />
        <OpioidTable
          activeProcedure={activeProcedure}
          data={tableData}
          handleTableSortClick={this.handleTableSortClick}
          loading={loading}
          sortedColumn={sortedColumn}
          sortOrder={sortOrder}
        />
        <div className="source">
          {`Source: ${sourceText}`}
        </div>
      </div>
    );
  }
}

ReactDOM.render(React.createElement(OpioidPrescribers, null), appElement);
